/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React from 'react'

import { TbArrowsExchange, TbBuilding, TbBuildingWarehouse, TbTruckDelivery, TbUsers } from 'react-icons/tb'
import { Link } from 'react-router-dom'

import { PageContainer, PageHeader } from '@/components/ui'

interface AdminSection {
  to: string
  label: string
  description: string
  icon: React.ReactNode
}

/** This is the place to add further admin tools. */
const SECTIONS: AdminSection[] = [
  {
    to: '/admin/users',
    label: 'Users',
    description: 'View Insights users and manage MyGeotab add-in sign-in',
    icon: <TbUsers className="h-6 w-6 text-accent" />,
  },
  {
    to: '/admin/companies',
    label: 'Companies',
    description: 'Set which apps and insights each company can use',
    icon: <TbBuilding className="h-6 w-6 text-accent" />,
  },
  {
    to: '/admin/asset-groups',
    label: 'Asset Groups',
    description: 'Limit apps and insights per asset group (sub-company)',
    icon: <TbBuildingWarehouse className="h-6 w-6 text-accent" />,
  },
  {
    to: '/admin/shipments/new',
    label: 'Tracker Shipments',
    description: 'Create a shipment for tracker monitoring',
    icon: <TbTruckDelivery className="h-6 w-6 text-accent" />,
  },
  {
    to: '/admin/migration',
    label: 'Migration Dashboard',
    description: 'Compare Fresh and Timescale endpoint by endpoint',
    icon: <TbArrowsExchange className="h-6 w-6 text-accent" />,
  },
]

const AdminPage: React.FC = () => (
  <PageContainer>
    <PageHeader title="Admin" subtitle="Cooltrax staff tools" />
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {SECTIONS.map((section) => (
        <Link
          key={section.to}
          to={section.to}
          className="card bg-base-100 shadow transition-shadow hover:shadow-lg"
          data-testid={`admin-section-${section.label.toLowerCase()}`}
        >
          <div className="card-body flex-row items-center gap-4">
            <span className="shrink-0">{section.icon}</span>
            <div>
              <h2 className="card-title text-base">{section.label}</h2>
              <p className="text-sm text-base-content/70">{section.description}</p>
            </div>
          </div>
        </Link>
      ))}
    </div>
  </PageContainer>
)

export default AdminPage
